/*

  e v e n t  l o g g i n g  i n t e r f a c e
  Event Logging Interface

  :description:
  A singleton class that acts as an interface for sending events to our pipelines.

*/

//
//  :code:
import { loadJSON } from '../js/requests'
import { talk } from '../js/talk'
import { kbSize, safeFromBase64 } from '../js/utils'

//
//  :statics:
//  Variables and static declarations.
export const EYEKANDY_LOCALISATION_INTERFACE = 'EyekandyLocalisationInterface'

//
//  :classes:
export class EyekandyLocalisationInterface {
  constructor (options) {
    if (typeof options === 'undefined') {
      options = {}
    }
    //
    //  :core:
    //  Core settings and values.
    this.name = 'eyekandy-localisation-interface'
    this.version = 'v3.0.0'
    this.group = '[EYEKANDY-LOCALISATION-INTERFACE]'

    //
    //  :runtime:
    this.language = options.language || 'en'
    this.environment = options.environment || 'dev'
    this.project = options.project || null

    //
    //  :v2:
    //  Translations, we store the translations here as language keys -> encoded strings.
    this.translations = {}

    //
    //  :echo:
    //  Write out into the console that the instance was created.
    talk([this.group, `created a new instance @ '${this.version}'`])
  }

  //
  //  :api:
  //  Exposed API functions for callers.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -
  getLocalisation (key, defaultLanguage, defaultValue) {
    //
    //  :step 1:
    //  Set our default to null if not defined.
    if (typeof defaultValue === 'undefined') {
      defaultValue = null
    }

    //
    //  :step 2:
    //  Now we can load our string, if it's not there we'll return the key value.
    const localised = this._getLocalisedString(key, defaultLanguage)

    //
    //  :step 3:
    //  If we didn't manage to read our key from our translations, and the default value is set - use the default
    //  value.
    if (localised === key && defaultValue) {
      return defaultValue
    }

    //
    //  All done, return the localised string to the caller.
    return localised
  }

  addLocalisedLanguage (language, phrases) {
    //
    //  :step 1:
    //  Throw any errors on invalid input.
    if (!language) {
      throw new Error(
        `${this.group} error, language is required, language:`,
        language
      )
    }
    if (!phrases) {
      throw new Error(
        `${this.group} error, phrases is required, phrases:`,
        phrases
      )
    }

    //
    //  TODO: Validate that phrases is a flat key/value map.
    //

    //
    //  :step 2:
    //  Store the definition for this language and its phrases.
    this.translations[language] = phrases

    //
    //  All done, return true to the caller.
    return true
  }

  async fetchRemoteJSONLocalisations (project, version) {
    project = this.project || project
    //
    //  :step 1:
    //  We need to build our URL.
    //  TODO: Tidy this.
    let url = null
    if (version === 'latest') {
      url = `https://js${
        this.environment === 'dev' ? '-dev' : ''
      }.pointandplace.com/json/latest/${project.replace(
        'eky-',
        ''
      )}-localisations/${project}-localisations.json`
    } else {
      url = `https://js${
        this.environment === 'dev' ? '-dev' : ''
      }.pointandplace.com/json/versioned/${project.replace(
        'eky-',
        ''
      )}-localisations/${project}-localisations-${version}.json`
    }
    //
    //  :step 2:
    //  Load this JSON, note, careful of cross origin restrictive environments as this is a live HTTPS request.
    talk([
      this.group,
      `starting to download our remote localisations JSON file '${url}'...`
    ])
    let localisations = await loadJSON(url)
    if (!localisations) {
      localisations = {}
    } else {
      talk([
        this.group,
        `ok, downloaded a localisation file of '${kbSize(
          JSON.stringify(localisations).length
        )}' containing '${
          Object.keys(localisations).length
        }' languages from '${url}'`
      ])
    }

    //
    //  :step 3:
    //  Ok, now we can save down our localisations.
    let languagesAdded = 0
    for (const [language, phrases] of Object.entries(localisations)) {
      this.addLocalisedLanguage(language, phrases)
      languagesAdded += 1
    }
    if (languagesAdded === 0) {
      talk(
        [
          this.group,
          `warning, we failed to load any localised languages from the remote localisations JSON file @ '${url}'`
        ],
        'warning'
      )
      return false
    } else {
      talk([
        this.group,
        `added a total of '${languagesAdded}' languages from the remote URL '${url}'`
      ])
      return true
    }
  }

  //
  //  :internal:
  //  Internal functions, shouldn't need to be called from outside of the class.
  //  -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -   =   -

  _transformLanguageToISOCode (language) {
    if (!language) {
      return language
    }
    if (language.includes('-')) {
      //
      //  en-EN -> en
      return language.split('-')[0]
    } else {
      //
      //  en -> en-EN
      if (language === 'en') {
        return `en-GB`
      }
      return `${language}-${language.toUpperCase()}`
    }
  }

  _hasLocalisation (key) {
    return typeof this.phrases[key] !== 'undefined'
  }

  _getLocalisedString (key, defaultLanguage) {
    //
    //  :step 1:
    //  Ensure our input parameters are valid.
    if (!defaultLanguage) {
      defaultLanguage = 'en'
    }
    if (!key) {
      throw new Error(
        `${this.group} error, a key is required to load a localised string, key:`,
        key
      )
    }
    //
    //  Store our encoded value in memory.
    let encodedValue = null

    //
    //  :step 2:
    //  Select the phrases for our set language, support transforming en into en-EN and vice-versa.
    const phrases = this.translations[this.language] || this.translations[this._transformLanguageToISOCode(this.language)]
    if (phrases) {
      //
      //  If we have a localised value, decode it and return it to the caller.
      if (phrases[key]) {
        encodedValue = phrases[key]
      }
    }

    //
    //  :step 3:
    //  If we don't have one, use the one from the defaultLanguage.
    const defaultPhrases = this.translations[defaultLanguage]
    if (!encodedValue && defaultPhrases) {
      //
      //  If we have a localised value, decode it and return it to the caller.
      if (defaultPhrases[key]) {
        encodedValue = defaultPhrases[key]
      }
    }

    //
    //  :step 4:
    //  Try to decode the value we have read from our translations.
    if (encodedValue) {
      try {
        encodedValue = safeFromBase64(encodedValue)
        return encodedValue
      } catch (err) {
        talk(
          [
            this.group,
            `error, the string at the key '${key}' in the language(s) '${this.language}, ${this.defaultLanguage}'`,
            `is badly encoded - we cannot read it`
          ],
          'warning'
        )
      }
    }

    //
    //  If there is no default language value one, use the key name instead.
    return key
  }
}
